import { useState } from 'react';
import { Billing, getEntityType, MarketplaceOffer, MeteringAlgorithm, Plan } from '@the-real-insight/tri-model';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogType, DialogFooter, TextField, DefaultButton, PrimaryButton, ComboBox, DatePicker } from '@fluentui/react';
import dayjs from 'dayjs';

interface UsePlanDialogProps {
    type: any;
    marketplaceOffer: MarketplaceOffer;
    setMarketplaceOffer: (marketplaceOffer: MarketplaceOffer) => void;
}

interface UsePlanDialogReturn {
    planDialogOpen: boolean;
    setPlanDialogOpen: (open: boolean) => void;
    selectedPlan: Plan | null;
    setSelectedPlan: (plan: Plan | null) => void;
    openPlanDialog: (plan?: Plan) => void;
    closePlanDialog: () => void;
    savePlan: () => void;
    PlanDialog: () => JSX.Element;
}

export function usePlanDialog({ type, marketplaceOffer, setMarketplaceOffer }: UsePlanDialogProps): UsePlanDialogReturn {
    const { t } = useTranslation();
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

    const openPlanDialog = (plan?: Plan) => {
        if (plan) {
            setSelectedPlan(plan);
        } else {
            setSelectedPlan(new Plan({
                name: 'Basis 3',
                description: 'Monatliche Grundgebühr von 15€ für 3 Nutzer, abzuschließen für 1 Jahr und zahlbar bei Abschluss, 5€ für jeden registrierten Nutzenden ab dem 4. Nutzenden in monatlicher Abrechnung.',
                baseFee: 15,
                term: 12,
                billings: [new Billing('', 5, 3, MeteringAlgorithm.perNamedUser)],
                validFromDate: new Date(),
                validToDate: dayjs().add(1, 'year').toDate(),
            }));
        }
        setPlanDialogOpen(true);
    };

    const closePlanDialog = () => {
        setPlanDialogOpen(false);
        setSelectedPlan(null);
    };

    const savePlan = () => {
        if (!selectedPlan) return;

        const updatedPlans = [...marketplaceOffer.plans];
        const planIndex = updatedPlans.findIndex(plan => plan === selectedPlan);

        if (planIndex === -1) {
            updatedPlans.push(selectedPlan);
        } else {
            updatedPlans[planIndex] = selectedPlan;
        }

        setMarketplaceOffer(new (getEntityType(type.name).type.type)({
            ...marketplaceOffer,
            plans: updatedPlans
        }));
        closePlanDialog();
    };

    const updatePlan = (updates: Partial<Plan>) => {
        if (!selectedPlan) return;
        setSelectedPlan({ ...selectedPlan, ...updates });
    };

    const PlanDialog = () => (
        <Dialog
            hidden={!planDialogOpen}
            onDismiss={closePlanDialog}
            maxWidth={1000}
            minWidth={800}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Plan bearbeiten',
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            <div>
                <div>
                    <TextField label="Name" value={selectedPlan?.name} onChange={(e, value) => {
                        updatePlan({ name: value || '' });
                    }} />
                </div>
                <div>
                    <TextField label="Beschreibung" multiline rows={3} value={selectedPlan?.description} onChange={(e, value) => {
                        updatePlan({ description: value || '' });
                    }} />
                </div>
                <div className="marginTopM displayFlex gapM">
                    <TextField
                        label="Basisgebühr"
                        type="number"
                        min={0}
                        step={0.01}
                        value={selectedPlan?.baseFee?.toString() || '0.00'}
                        onChange={(e, value) => {
                            const numValue = value ? parseFloat(value) : 0;
                            updatePlan({ baseFee: numValue });
                        }}
                        suffix="€"
                    />
                    <TextField
                        label="Inklusive Nutzer"
                        type="number"
                        min={1}
                        step={1}
                        value={selectedPlan?.billings?.[0]?.offset?.toString() || '1'}
                        onChange={(e, value) => {
                            const numValue = value ? parseInt(value) : 1;
                            updatePlan({ billings: [new Billing('', selectedPlan?.billings?.[0]?.offset || 0, numValue, MeteringAlgorithm.perNamedUser)] });
                        }}
                    />
                    <TextField
                        label="Monatsgebühr zusätzliche Nutzer"
                        type="number"
                        min={0}
                        step={0.01}
                        value={selectedPlan?.billings?.[0]?.fee?.toFixed(2) || '0.00'}
                        onChange={(e, value) => {
                            const numValue = value ? parseFloat(value) : 0;
                            updatePlan({ billings: [new Billing('', numValue, selectedPlan?.billings?.[0]?.fee || 0, MeteringAlgorithm.perNamedUser)] });
                        }}
                        suffix="€"
                    />
                </div>
                <div className="marginTopM textM colorPrimary">Die Basisgebühr wird jährlich vorab mit der ersten Rechnung abgerechnet.</div>
                <div className="marginTopM displayFlex gapM">
                    <ComboBox
                        label="Abrechnungszeitraum"
                        selectedKey={selectedPlan?.term?.toString()}
                        options={[
                            { key: '1', text: '1 Monat' },
                            { key: '3', text: '3 Monate' },
                            { key: '6', text: '6 Monate' },
                            { key: '12', text: '12 Monate' }
                        ]}
                        onChange={(e, option) => {
                            if (option?.key) {
                                updatePlan({ term: parseInt(String(option.key)) });
                            }
                        }}
                    />
                    <DatePicker
                        label="Gültig ab"
                        value={selectedPlan?.validFromDate ? new Date(selectedPlan.validFromDate) : undefined}
                        onSelectDate={(date) => {
                            if (date) {
                                updatePlan({ validFromDate: date });
                            }
                        }}
                        formatDate={(date) => date ? dayjs(date).format('DD.MM.YY') : ''}
                        placeholder="Datum auswählen..."
                        styles={{ root: { width: '250px' } }}
                    />
                </div>
            </div>
            <DialogFooter className="marginTopXXL">
                <DefaultButton onClick={closePlanDialog} text={t('global.close')} />
                <PrimaryButton onClick={savePlan} text={t('global.save')} />
            </DialogFooter>
        </Dialog>
    );

    return {
        planDialogOpen,
        setPlanDialogOpen,
        selectedPlan,
        setSelectedPlan,
        openPlanDialog,
        closePlanDialog,
        savePlan,
        PlanDialog
    };
} 